// Offered to the user near the end of a free call. Pays for a paid package
// through the same consultationPayment + StripeCardModal flow as starting a
// paid call, then attaches it to the live call via POST /extendCall — the
// call keeps running underneath the whole time.
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import StripeCardModal from "../user/StripeCardModal";
import { apiErrorMessage, userService } from "../../services/userApi";

const isFree = (p) => !!p && (!!p.is_free || Number(p.price) === 0);
const fmtPrice = (p) => `${p.currency || "USD"} ${Number(p.price)}`;

export default function ExtendCallSheet({ call, remaining, onClose, onEndCall, onExtended }) {
  const [packages, setPackages] = useState(null);
  const [selected, setSelected] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    let active = true;
    userService
      .getCallPackages()
      .then((res) => {
        if (!active) return;
        const paid = (res?.data?.data || []).filter((p) => !isFree(p));
        setPackages(paid);
        setSelected((cur) => cur || paid[0] || null);
      })
      .catch(() => {
        if (!active) return;
        setPackages([]);
        toast.error("Call durations couldn't be loaded.");
      });
    return () => {
      active = false;
    };
  }, []);

  const minutesLeft = Math.max(1, Math.ceil(remaining / 60));
  const lawyerName = call.lawyer?.company_name || call.lawyer?.name;

  return (
    <>
      {!payOpen && (
        <div className="lp-extend-overlay" onClick={onClose}>
          <div className="lp-extend-sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <span className="lp-extend-grip" />
            <button type="button" className="lp-extend-x" onClick={onClose} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>

            <div className="lp-extend-head">
              <span className="lp-extend-clock"><i className="bi bi-clock" /></span>
              <div>
                <h3>Your Free Call Is Ending Soon</h3>
                <p>
                  You have {minutesLeft} {minutesLeft === 1 ? "minute" : "minutes"} remaining. Would you like to
                  continue your consultation{lawyerName ? ` with ${lawyerName}` : ""}?
                </p>
              </div>
            </div>

            {packages === null ? (
              <div className="lp-extend-loading"><span className="spinner-border spinner-border-sm" /></div>
            ) : packages.length === 0 ? (
              <p className="lp-extend-empty">No paid plans are available right now.</p>
            ) : (
              <div className="lp-extend-plans" role="radiogroup" aria-label="Extension plan">
                {packages.map((p) => {
                  const active = selected?.id === p.id;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      role="radio"
                      aria-checked={active}
                      className={active ? "lp-extend-plan selected" : "lp-extend-plan"}
                      onClick={() => setSelected(p)}
                    >
                      <span className="lp-extend-radio" />
                      <span className="lp-extend-plan-name">
                        <strong>{p.duration_minutes} Minutes</strong>
                        {(p.description || p.title) && <small>{p.description || p.title}</small>}
                      </span>
                      <span className="lp-extend-plan-price">{fmtPrice(p)}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="lp-extend-features">
              <div><i className="bi bi-people-fill" /><span>Continue with the same lawyer</span></div>
              <div><i className="bi bi-shield-fill-check" /><span>Secure payment with Stripe</span></div>
              <div><i className="bi bi-lightning-charge-fill" /><span>Call will be automatically extended</span></div>
            </div>

            <button type="button" className="lp-extend-pay" disabled={!selected} onClick={() => setPayOpen(true)}>
              Select &amp; Pay to Continue
            </button>
            <button type="button" className="lp-extend-no" onClick={onEndCall}>
              No Thanks, End Free Call
            </button>
          </div>
        </div>
      )}

      <StripeCardModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        rootClassName="lp-above-call"
        title="Extend Call"
        amountLabel={selected ? fmtPrice(selected) : ""}
        submitLabel="Pay & Extend"
        billing={{ name: call.user?.name, email: call.user?.email }}
        note="Your card is only held now — charged after your call ends."
        prepare={() => userService.consultationPayment(selected.price, selected.id)}
        onPaid={async (pi) => {
          let res;
          try {
            res = await userService.extendCall(call.callId, selected.id, pi.id);
          } catch (err) {
            throw new Error(apiErrorMessage(err, "The call could not be extended. Your card was not charged."));
          }
          if (!res?.data?.status) {
            throw new Error(res?.data?.message || "The call could not be extended. Your card was not charged.");
          }
          onExtended(Number(res.data.data?.extended_minutes) || Number(selected.duration_minutes) || 0);
        }}
      />
    </>
  );
}
